import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { FactStatus } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

export const EvidenceSources = ({ sources, selected, onSave }) => {
  const [all, setAll] = useState(!selected.length);
  const [ids, setIds] = useState(selected.length ? selected : sources.filter((s) => s.active).map((s) => s.id));
  return (
    <section className="surface space-y-4 p-5" data-testid="evidence-sources-section">
      <div><h2 className="text-2xl">Approved evidence</h2><p className="mt-1 text-sm text-slate-500">Claims are only checked against articles from these sources. Inactive sources are never used.</p></div>
      <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={all} onCheckedChange={setAll} data-testid="evidence-all-switch" />Use every active approved source</label>
      {!all && <div className="grid gap-2 sm:grid-cols-3" data-testid="evidence-source-list">{sources.map((s) => (
        <label key={s.id} className={`flex items-center gap-2 text-sm ${s.active ? "" : "opacity-50"}`}>
          <Checkbox checked={ids.includes(s.id)} disabled={!s.active} onCheckedChange={(c) => setIds(c ? [...ids, s.id] : ids.filter((x) => x !== s.id))} data-testid={`evidence-source-${s.id}`} />{s.name}
        </label>
      ))}</div>}
      <Button className="rounded-full" disabled={!all && !ids.length} onClick={() => onSave(all ? [] : ids)} data-testid="evidence-save">Save evidence sources</Button>
    </section>
  );
};

export const FactQueue = ({ onChange }) => {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/factcheck/queue").then((r) => setQ(r.data));
  useEffect(() => { load(); }, []);
  const check = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post(`/admin/factcheck/items/${it.collection}/${it.id}`); toast.success(`Checked: ${data.fact_check?.status || "no claims"}`); load(); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  if (!q) return null;
  return (
    <section className="space-y-3" data-testid="fact-queue-section">
      <h2 className="text-2xl">Not yet checked ({q.items.length})</h2>
      <p className="text-sm text-slate-500">Checked automatically once reach hits {q.threshold}. You can check any item now.</p>
      {q.items.map((it) => (
        <div key={it.id} className="surface flex flex-wrap items-start gap-3 p-4" data-testid={`fact-queue-item-${it.id}`}>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs uppercase tracking-wider text-slate-400">{it.collection.slice(0, -1)} · reach {it.reach}{it.fact_check.reason ? ` · ${it.fact_check.reason.replace("_", " ")}` : ""}</p>
            <p className="text-sm text-slate-800">{it.text}</p>
            {(it.fact_check.claims || []).map((c, i) => <p key={i} className="text-xs text-slate-500">• {c.text} <span className="capitalize">({c.topic}, {c.risk} risk)</span></p>)}
          </div>
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy === it.id} onClick={() => check(it)} data-testid={`fact-queue-check-${it.id}`}>{busy === it.id ? "Checking…" : "Check now"}</Button>
        </div>
      ))}
    </section>
  );
};

export const FactDatabase = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const load = (text = q) => api.get("/admin/factcheck/claims", { params: { q: text } }).then((r) => setRows(r.data.claims));
  useEffect(() => { load(""); }, []); // eslint-disable-line
  const expire = async (id) => { try { await api.post(`/admin/factcheck/claims/${id}/expire`); toast.success("It will be checked again next time it appears"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="space-y-3" data-testid="fact-database-section">
      <h2 className="text-2xl">Middle fact database</h2>
      <p className="text-sm text-slate-500">Every checked claim is stored once and reused across posts, shorts and news, so the same claim is never paid for twice while it's current.</p>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search claims" data-testid="fact-db-search" />
        <Button type="submit" variant="outline" className="rounded-full" data-testid="fact-db-search-btn">Search</Button>
      </form>
      {!rows.length && <p className="text-sm text-slate-500" data-testid="fact-db-empty">No claims yet.</p>}
      {rows.map((c) => {
        const live = c.expires_at > new Date().toISOString();
        return (
          <div key={c.id} className="surface space-y-1.5 p-4" data-testid={`fact-db-row-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2"><FactStatus status={c.verdict} /><span className="text-xs text-slate-500">{c.origin === "news" ? "From a news check" : `Member claim · ${c.topic}, ${c.risk} risk`} · used {c.uses || 0}× · {c.model} · checked {timeAgo(c.checked_at)} · {live ? "current" : "expired"}</span></div>
            <p className="text-sm text-slate-800">{c.text}</p>
            {c.explanation && <p className="text-xs text-slate-500">{c.explanation}</p>}
            {live && <Button size="sm" variant="ghost" className="h-7 rounded-full px-3 text-xs" onClick={() => expire(c.id)} data-testid={`fact-db-expire-${c.id}`}>Check again next time</Button>}
          </div>
        );
      })}
    </section>
  );
};
